import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Novel } from "@/lib/novel";
import { formatCount, formatWordCount } from "@/lib/novel";

export default function HeroCarousel({ items }: { items: Novel[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const novel = items[active];

  return (
    <section className="relative overflow-hidden bg-primary-950">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-accent-500/20 blur-3xl"></div>
      <div className="pointer-events-none absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-primary-500/30 blur-3xl"></div>

      <div className="relative w-full px-4 py-10 md:px-8 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
          <div key={novel.id} className="animate-marquee-fade order-2 lg:order-1">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-300">
              <i className="ri-star-fill"></i>
              本期焦点推荐
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {novel.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-300">
              <span>
                <i className="ri-user-line mr-1"></i>
                {novel.author}
              </span>
              <span>
                <i className="ri-price-tag-3-line mr-1"></i>
                {novel.categoryName}
              </span>
              <span>
                <i className="ri-file-text-line mr-1"></i>
                {formatWordCount(novel.wordCount)}
              </span>
              <span className="rounded-sm bg-background-50/10 px-2 py-0.5">{novel.status}</span>
            </div>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
              {novel.intro}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/read/${novel.id}/${novel.chapters[0]?.chapterId ?? "c1"}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </Link>
              <Link
                to={`/detail/${novel.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-information-line"></i>
                查看详情
              </Link>
              <span className="flex items-center gap-2 text-xs text-background-400">
                <i className="ri-fire-line text-accent-400"></i>
                {formatCount(novel.clicks)} 次阅读 · {formatCount(novel.favorites)} 收藏
              </span>
            </div>
          </div>

          <div className="order-1 flex justify-center lg:order-2 lg:justify-end">
            <div className="relative">
              <div className="absolute inset-0 -rotate-3 rounded-xl bg-accent-500/30"></div>
              <img
                key={`${novel.id}-cover`}
                src={novel.cover}
                alt={`${novel.title} 封面`}
                title={`${novel.title} 在线阅读 - 御书屋`}
                className="animate-fade-in relative h-[360px] w-[270px] rounded-xl object-top md:h-[420px] md:w-[310px]"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => setActive(index)}
              className={`h-1.5 rounded-full transition-all ${
                index === active
                  ? "w-10 bg-accent-500"
                  : "w-5 bg-background-50/30 hover:bg-background-50/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}